/**
 * Hook that provides text-to-speech prompts for the squawk player based on its current state
 * @param status - Current connection status of the squawk player
 * @param channelName - Name of the channel being connected to
 */
import { textToSpeech } from "@/utils/tts-utils";
import { useEffect, useRef } from "react";

type SquawkStatus = "connected" | "disconnected" | "error" | "idle";

// Helper to check if audio announcements are enabled in localStorage
const areAudioAnnouncementsEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem("squawk.audioAnnouncements");
    // Default to true if not set
    return stored === null ? true : JSON.parse(stored);
  } catch {
    // Default to true if localStorage fails
    return true;
  }
};

export const useSquawkAudioPrompts = (
  status: SquawkStatus,
  channelName: string,
  options?: { isInDemoMode?: boolean }
) => {
  const prevStatusRef = useRef<SquawkStatus | null>(null);

  useEffect(() => {
    // Only trigger prompts when status actually changes, not when channel name changes
    const statusChanged = prevStatusRef.current !== status;
    prevStatusRef.current = status;

    if (!statusChanged) return;
    if (!channelName && status === "connected") return;

    // Skip announcements in demo mode to avoid overlap with demo audio
    if (options?.isInDemoMode) return;

    // Check if audio announcements are enabled in user preferences
    if (!areAudioAnnouncementsEnabled()) {
      // User has disabled audio announcements - skip TTS
      return;
    }

    // Async function to speak prompt
    // Note: textToSpeech internally calls speechSynthesis.cancel() to stop any previous speech
    const speakPrompt = async (text: string) => {
      try {
        await textToSpeech({ text });
      } catch (error) {
        console.error("Failed to speak prompt:", error);
      }
    };

    // Trigger appropriate prompt based on status
    switch (status) {
      case "connected":
        speakPrompt(`Connected to ${channelName} channel.`);
        break;
      case "disconnected":
        speakPrompt("Audio squawk disconnected.");
        break;
      case "error":
        speakPrompt("Audio squawk error. Please check your connection.");
        break;
      default:
        break;
    }

    // Cleanup: cancel any active speech when status changes or unmount
    // This ensures we don't have overlapping TTS from rapid status changes
    return () => {
      speechSynthesis.cancel();
    };
  }, [status, channelName]);
};
